"use client";

import { ArrowRight, Check, X } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Link from "@/components/ui/link";
import { formatAbsoluteDay } from "@/lib/dates/relative-time";
import {
  MemberNextStepsActions,
  type MemberNextStepsData,
} from "./member-next-steps-actions";

type MemberViewProps = {
  hiiveName: string;
  hiiveSlug: string;
  nextSteps: MemberNextStepsData | null;
};

export function PaidMemberView({
  hiiveName,
  hiiveSlug,
  nextSteps,
}: MemberViewProps) {
  return (
    <MemberLandingView
      heading="You're in"
      body={`Your membership at ${hiiveName} is active. Here's what it opens.`}
      hiiveName={hiiveName}
      hiiveSlug={hiiveSlug}
      nextSteps={nextSteps}
    />
  );
}

/**
 * One shape for both "you are a member" screens.
 *
 * Free and paid differ by a sentence, not by a layout — and when they were two
 * hand-written blocks the free one quietly shipped with no way forward at all.
 * Sharing the frame means a destination added here can never again exist on
 * only one of them.
 */
function MemberLandingView({
  heading,
  body,
  hiiveName,
  hiiveSlug,
  nextSteps,
}: MemberViewProps & { heading: string; body: string }) {
  return (
    <div className="flex flex-1 flex-col justify-center py-8">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-success-bg">
            <Check className="size-6 text-success" aria-hidden />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
            {heading}
          </h2>
          <p className="text-sm text-balance text-text-secondary">{body}</p>
        </div>
        <MemberNextStepsActions
          hiiveName={hiiveName}
          hiiveSlug={hiiveSlug}
          nextSteps={nextSteps}
        />
      </div>
    </div>
  );
}

interface PendingJoinRequestSummary {
  id: number;
  tierName: string;
  requestedAt: string;
  expiresAt: string | null;
}

interface PendingMemberViewProps {
  pendingJoinRequest: PendingJoinRequestSummary | null;
  onCancel?: () => void;
  isCancelling?: boolean;
}

function formatDate(dateLike: string | null | undefined) {
  if (!dateLike) return null;
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return null;
  return formatAbsoluteDay(date);
}

export function PendingMemberView({
  pendingJoinRequest,
  onCancel,
  isCancelling = false,
}: PendingMemberViewProps) {
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const requestedOn = formatDate(pendingJoinRequest?.requestedAt);
  const expiresOn = formatDate(pendingJoinRequest?.expiresAt);

  return (
    <div className="flex flex-1 flex-col justify-center py-4">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-5">
        <div className="space-y-3 text-left">
          <Badge
            variant="outline"
            className="pointer-events-none rounded-full border-glass-border-10 bg-glass-bg-02 px-2.5 py-0.5 text-[11px] font-medium text-text-secondary"
          >
            Waiting on the organisers
          </Badge>
          <div className="space-y-1.5">
            <h2 className="text-2xl leading-tight font-bold tracking-tight text-text-primary sm:text-3xl">
              {pendingJoinRequest
                ? `You've asked to join ${pendingJoinRequest.tierName}`
                : "Your request is in"}
            </h2>
            {/*
              "You will be notified once it is approved" promised an outcome
              nobody had decided yet. They hear either way, and saying so is
              the difference between waiting and waiting in the dark.
            */}
            <p className="max-w-lg text-sm leading-relaxed text-text-secondary">
              They have it now. We'll tell you as soon as they answer —
              whichever way it goes.
            </p>
          </div>
        </div>

        {pendingJoinRequest ? (
          <div className="border-y border-glass-border-10">
            <div className="flex items-center justify-between gap-4 py-2.5">
              <span className="text-xs font-medium tracking-wider text-text-tertiary uppercase">
                Membership
              </span>
              <span className="text-right text-sm font-bold text-text-primary">
                {pendingJoinRequest.tierName}
              </span>
            </div>
            {requestedOn ? (
              <div className="flex items-center justify-between gap-4 border-t border-glass-border-10 py-2.5">
                <span className="text-xs font-medium tracking-wider text-text-tertiary uppercase">
                  Sent
                </span>
                <span className="text-right text-sm font-bold text-text-primary">
                  {requestedOn}
                </span>
              </div>
            ) : null}
            {expiresOn ? (
              <div className="flex items-center justify-between gap-4 border-t border-glass-border-10 py-2.5">
                <span className="text-xs font-medium tracking-wider text-text-tertiary uppercase">
                  Closes if unanswered
                </span>
                <span className="text-right text-sm font-bold text-text-primary">
                  {expiresOn}
                </span>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="grid w-full gap-2.5 sm:grid-cols-2">
          {pendingJoinRequest ? (
            <Button
              asChild
              type="button"
              className="flex h-10 w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl bg-brand bg-gradient-to-r from-brand to-[#8855ff] text-xs font-semibold text-text-inverse shadow-[0_4px_16px_rgba(106,51,255,0.22)] transition-all duration-300 hover:scale-[1.01] hover:to-[#9966ff] hover:shadow-[0_4px_22px_rgba(106,51,255,0.4)] active:scale-[0.98]"
              style={{ backgroundColor: "var(--color-brand)" }}
            >
              <Link href={`/membership-requests/${pendingJoinRequest.id}`}>
                <span>View your request</span>
                <ArrowRight className="size-3.5" />
              </Link>
            </Button>
          ) : null}
          {onCancel ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmingCancel(true)}
              disabled={isCancelling}
              className="flex h-10 w-full cursor-pointer items-center justify-center gap-1.5 rounded-xl border border-glass-border-08 bg-glass-bg-02 text-xs font-semibold text-text-secondary transition-all duration-300 hover:scale-[1.01] hover:border-glass-border-20 hover:bg-glass-bg-06 hover:text-text-primary active:scale-[0.98]"
            >
              <X className="size-3.5" />
              Withdraw request
            </Button>
          ) : null}
        </div>

        {/*
          The same two-step confirm the request page uses.

          This button used to withdraw immediately, while the identical action
          one screen away sat behind a confirm and a warning — so the more
          accidental tap was the unguarded one. Destroying a request should
          take the same deliberateness wherever it is offered.
        */}
        {onCancel && confirmingCancel ? (
          <div className="space-y-3 rounded-2xl border border-glass-border-10 bg-glass-bg-01 p-4">
            <p className="text-sm text-text-secondary">
              Withdraw this request? The organisers will no longer see it, and
              you'd need to ask again from this page.
            </p>
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmingCancel(false)}
                disabled={isCancelling}
                className="h-10 flex-1 rounded-xl text-xs"
              >
                Keep it
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => {
                  setConfirmingCancel(false);
                  onCancel();
                }}
                disabled={isCancelling}
                className="h-10 flex-1 rounded-xl text-xs"
              >
                {isCancelling ? "Withdrawing…" : "Yes, withdraw it"}
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function FreeMemberView({
  tierName,
  hiiveName,
  hiiveSlug,
  nextSteps,
}: MemberViewProps & { tierName?: string }) {
  return (
    <MemberLandingView
      heading="You're in"
      /*
        "The organiser will confirm pricing" was left over from the enquiry
        model. There is nothing left to confirm: the membership is on.
      */
      body={
        tierName
          ? `You're a ${tierName} member at ${hiiveName}. Here's what it opens.`
          : `Your membership at ${hiiveName} is active. Here's what it opens.`
      }
      hiiveName={hiiveName}
      hiiveSlug={hiiveSlug}
      nextSteps={nextSteps}
    />
  );
}
